import React, { useState } from 'react';
import Seo from '../components/Seo';
import { useSearchParams } from 'react-router-dom';
import { Send, CheckCircle2, MessageCircle, ShieldCheck, ArrowRight } from 'lucide-react';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import { productOptions, certifications } from '../mock/mock';
import { api } from '../lib/api';
import { waDisplay, openWhatsApp } from '../lib/wa';

const Quote = () => {
  const [params] = useSearchParams();
  const preProduct = params.get('product') || '';
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', country: '', product: preProduct, quantity: '', port: '', incoterm: 'FOB', packing: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.product) e.product = 'Select a product';
    if (!form.quantity.trim()) e.quantity = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (!validate()) { window.scrollTo({ top: 260, behavior: 'smooth' }); return; }
    api.post('/enquiries', { type: 'quote', ...form })
      .then(() => { setSent(true); window.scrollTo({ top: 0, behavior: 'smooth' }); })
      .catch(() => { alert('Something went wrong. Please try again or contact us on WhatsApp.'); });
  };

  return (
    <div>
      <Seo title="Request a Quote — FOB / CIF Export Pricing" description="Request an export quote from Kassam Trading Company for rice, salt, wheat, corn or sesame. FOB Port Qasim and CIF pricing available to major ports worldwide." path="/quote" />
      <PageHero
        title="Request a Quote"
        subtitle="Tell us your product, quantity and destination, and our team responds fast with competitive FOB Port Qasim / CIF pricing."
        crumb="Request a Quote"
      />

      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-10">
        <Reveal className="lg:col-span-2">
          <div className="bg-ktc-ivoryLight rounded-2xl border border-ktc-cream p-7 md:p-9">
            {sent ? (
              <div className="text-center py-16">
                <CheckCircle2 className="mx-auto text-ktc-green" size={60} />
                <h3 className="font-serif text-3xl font-semibold text-ktc-green mt-4">Quote Request Received</h3>
                <p className="text-ktc-charcoal/70 mt-3 max-w-md mx-auto">Thank you. Our export team will review your requirement and get back to you shortly with pricing and details.</p>
                <div className="mt-7 flex flex-wrap justify-center gap-4">
                  <button type="button" onClick={() => openWhatsApp()} data-testid="quote-success-whatsapp-button" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-medium px-6 py-3 rounded-md"><MessageCircle size={17} /> Chat on WhatsApp</button>
                  <button onClick={() => setSent(false)} className="inline-flex items-center gap-2 border border-ktc-green/25 text-ktc-green font-medium px-6 py-3 rounded-md hover:border-ktc-green transition-colors">New request</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                <h2 className="font-serif text-3xl font-semibold text-ktc-green mb-6">Tell us your requirement</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full Name" required error={errors.name}><input value={form.name} onChange={(e) => update('name', e.target.value)} className="ktc-input" placeholder="Your name" /></Field>
                  <Field label="Email" required error={errors.email}><input value={form.email} onChange={(e) => update('email', e.target.value)} className="ktc-input" placeholder="you@company.com" /></Field>
                  <Field label="Phone / WhatsApp"><input value={form.phone} onChange={(e) => update('phone', e.target.value)} className="ktc-input" placeholder="+.." /></Field>
                  <Field label="Company"><input value={form.company} onChange={(e) => update('company', e.target.value)} className="ktc-input" placeholder="Company" /></Field>
                  <Field label="Country"><input value={form.country} onChange={(e) => update('country', e.target.value)} className="ktc-input" placeholder="Destination country" /></Field>
                  <Field label="Destination Port"><input value={form.port} onChange={(e) => update('port', e.target.value)} className="ktc-input" placeholder="e.g. Jebel Ali" /></Field>
                  <Field label="Product" required error={errors.product}>
                    <select value={form.product} onChange={(e) => update('product', e.target.value)} className="ktc-input">
                      <option value="">Select a product</option>
                      {productOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Quantity (Tons)" required error={errors.quantity}><input value={form.quantity} onChange={(e) => update('quantity', e.target.value)} className="ktc-input" placeholder="e.g. 100" /></Field>
                  <Field label="Incoterm">
                    <select value={form.incoterm} onChange={(e) => update('incoterm', e.target.value)} className="ktc-input">
                      {['FOB', 'CIF', 'CFR', 'EXW'].map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </Field>
                  <Field label="Packing Preference"><input value={form.packing} onChange={(e) => update('packing', e.target.value)} className="ktc-input" placeholder="e.g. 25kg PP bags" /></Field>
                </div>
                <Field label="Additional Details"><textarea value={form.message} onChange={(e) => update('message', e.target.value)} rows={4} className="ktc-input resize-none" placeholder="Any specifications, private label needs, timelines..." /></Field>
                <button type="submit" className="inline-flex items-center gap-2 bg-ktc-green hover:bg-ktc-greenLight text-ktc-ivory font-semibold px-8 py-3.5 rounded-md mt-3 transition-colors"><Send size={17} /> Submit Request</button>
              </form>
            )}
          </div>
        </Reveal>

        <Reveal delay={100} className="space-y-6">
          <div className="bg-ktc-green text-ktc-ivory rounded-2xl p-7">
            <h3 className="font-serif text-2xl font-semibold">Why buyers choose KTC</h3>
            <ul className="mt-5 space-y-3.5 text-sm text-ktc-ivory/85">
              {['Own mill at Port Qasim', 'FOB Port Qasim · CIF worldwide', 'Private label & custom packing', '30+ countries served since 1999'].map((t) => (
                <li key={t} className="flex gap-3"><ShieldCheck size={18} className="text-ktc-goldLight shrink-0" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="bg-ktc-ivoryLight rounded-2xl border border-ktc-cream p-7">
            <h3 className="font-serif text-2xl font-semibold text-ktc-green">Prefer to talk?</h3>
            <p className="text-sm text-ktc-charcoal/70 mt-2">Reach us instantly on WhatsApp.</p>
            <button type="button" onClick={() => openWhatsApp()} data-testid="quote-sidebar-whatsapp-button" className="inline-flex items-center gap-2 bg-[#25D366] text-white font-medium px-6 py-3 rounded-md mt-4"><MessageCircle size={17} /> {waDisplay()}</button>
            <div className="flex flex-wrap gap-2 mt-6">
              {certifications.slice(0, 4).map((c) => <span key={c.abbr} className="text-xs bg-ktc-cream/70 text-ktc-green font-medium px-3 py-1.5 rounded-full">{c.abbr}</span>)}
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

const Field = ({ label, required, error, children }) => (
  <label className="block mb-4">
    <span className="text-sm font-medium text-ktc-charcoal/80">{label}{required && <span className="text-ktc-gold"> *</span>}</span>
    <div className="mt-1.5">{children}</div>
    {error && <span className="text-xs text-red-600 mt-1 block">{error}</span>}
  </label>
);

export default Quote;
